<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { AUTHMODELHS } from '@/utils/models'
import { useAuthStore } from '@/stores/auth.store'
import { useIdeStore } from '@/stores/ide.store'

import utilModelsService from '@/services/util.models.service'
import { onboardingSave } from '@/services/onboarding.service'
import authService from '@/services/auth.service'

import OnboardingProfessionalDetailsBanner from '@/components/header/auth/AuthOnboardingAdvBanner/OnboardingProfessionalDetailsBanner.vue'
import {
  ONBOARDING_STEPS,
  ONBOARDING_PROFESSIONAL_ROLES
} from '@/components/header/auth/OnboardingFlow/enum/onboarding.enum'

import type { IOnboardingSave } from '@/components/organisation/interface/IOnboardingSteps'
import BaseTextbox from '@/components/forms/BaseTextbox.vue'
import Button from '@/components/atoms/button/index.vue'
import { string } from 'yup'
import { useField, useForm } from 'vee-validate'
import { HttpStatusCode } from 'axios'
import { useRouter } from 'vue-router'
import { isEmpty } from 'lodash-es'

const router = useRouter()
const props = defineProps({
  currentOnboardingSteps: {
    type: Function,
    required: true
  },
  prefilledValues: {
    type: Object,
    required: false
  },

  invitations: {
    type: Array,
    required: false
  },
  alreadyCreatedOrganization: {
    type: Boolean,
    required: false
  }
})

const authStore = useAuthStore()
const ideStore = useIdeStore()

const { errors, handleSubmit } = useForm()

const { value: description, errorMessage: descriptionError } = useField('description', string())

const descriptionInput = {
  label: 'Others',
  placeholder: 'Can you describe your current role?'
}

const isLoading = ref<boolean>(false)
const httpError = ref<string>('')
const showOthersTextBox = ref<boolean>(false)
const showOthersTextBoxError = ref<string>('')
const selectedOptionError = ref<string>('')
const selectedOption = ref<string>('')

const professionalRoles = computed(() => {
  return Object.values(ONBOARDING_PROFESSIONAL_ROLES).map((value) => ({
    value,
    label: value.charAt(0).toUpperCase() + value.slice(1)
  }))
})

const canSubmit = computed(() => {
  return Object.keys(errors.value).length === 0 && !isLoading.value
})

/**
 * updated the final onboarded value
 * @returns final onboarded boolean value
 */
const handleOnboardedValue = () => {
  if (authStore.mobileDeviceLogin || authStore.chromeExtensionLogin) {
    return true
  } else if (
    selectedOption.value == ONBOARDING_PROFESSIONAL_ROLES.TEACHERORPROFRESSIONAL &&
    !authStore.isPremium
  ) {
    return false
  } else if (
    selectedOption.value == ONBOARDING_PROFESSIONAL_ROLES.SOFTWAREDEVELOPER &&
    !authStore.isPremium
  ) {
    return false
  } else {
    return true
  }
}

/**
 * Close the modal & call init auth
 */
const handleSkip = () => {
  ideStore.setBoardingAfterLogin(true)
  utilModelsService.closeModal(AUTHMODELHS.ONBOARDING)
  authService.initAuth()
}

const onSubmit = handleSubmit(async () => {
  if (isEmpty(selectedOption.value)) {
    selectedOptionError.value = 'This field is required'
    return
  }

  if (showOthersTextBox.value && isEmpty(description.value)) {
    showOthersTextBoxError.value = 'Please enter your role'
    return
  } else {
    showOthersTextBoxError.value = ''
  }

  let requestObj: IOnboardingSave = {
    firstName: props.prefilledValues?.firstName || authStore.onboardingSteps.firstName,
    lastName: props.prefilledValues?.lastName || authStore.onboardingSteps.lastName,
    occupation: showOthersTextBox.value ? (description.value as string) : selectedOption.value,
    onboarded: handleOnboardedValue(),
    step: 3
  }

  httpError.value = ''
  isLoading.value = true
  await onboardingSave(requestObj)
    .then(async (res: any) => {
      if (res?.status == HttpStatusCode.Ok) {
        if (selectedOption.value == ONBOARDING_PROFESSIONAL_ROLES.TEACHERORPROFRESSIONAL) {
          if (authStore.mobileDeviceLogin || authStore.chromeExtensionLogin) {
            utilModelsService.closeModal(AUTHMODELHS.ONBOARDING)
            await authService.initAuth()
          } else {
            props?.currentOnboardingSteps(ONBOARDING_STEPS.TEACHER_ORGANISATION, requestObj)
          }
        } else if (selectedOption.value == ONBOARDING_PROFESSIONAL_ROLES.SOFTWAREDEVELOPER) {
          if (authStore.mobileDeviceLogin || authStore.chromeExtensionLogin) {
            utilModelsService.closeModal(AUTHMODELHS.ONBOARDING)
            await authService.initAuth()
          } else {
            if (authStore.isPremium) {
              utilModelsService.closeModal(AUTHMODELHS.ONBOARDING)
              await authService.initAuth()
              router.push({ name: 'start-coding' })
            } else {
              props?.currentOnboardingSteps(
                ONBOARDING_STEPS.SOFTWARE_DEVELOPER_PRO_PLAN,
                requestObj
              )
            }
          }
        } else {
          // redirect to default IDE page
          utilModelsService.closeModal(AUTHMODELHS.ONBOARDING)
          await authService.initAuth()
          if (!authStore.mobileDeviceLogin && !authStore.chromeExtensionLogin) {
            router.push({ name: 'start-coding' })
          }
        }
      }
    })
    .catch(() => {
      httpError.value = 'Something went wrong please try again'
    })
    .finally(() => {
      isLoading.value = false
    })
})

/**
 * Handle change event of radio button
 */
function handleChange() {
  if (selectedOption.value === ONBOARDING_PROFESSIONAL_ROLES.OTHERS) {
    showOthersTextBox.value = true
  } else {
    showOthersTextBox.value = false
  }
}

/**
 * Move current step page to previous page
 */
const handlePrevious = () => {
  props.currentOnboardingSteps(ONBOARDING_STEPS.PERSONAL)
}
/**
 * Add Box Shadow to particular dom element
 * @param event capture current element and add style to it
 */
const addBoxShadow = (event: any) => {
  event.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.6)'
}

/**
 * Remove Box Shadow to particular dom element
 * @param event capture current element and remove style to it
 */
const removeBoxShadow = (event: any) => {
  event.currentTarget.style.boxShadow = ''
}
/**
 * professional role name based on the provided role identifier.
 * @param name - The role identifier to convert.
 * @returns professional role name.
 */
const showProfessionalNames = (name: string) => {
  if (name == ONBOARDING_PROFESSIONAL_ROLES.TEACHERORPROFRESSIONAL) {
    return 'Teacher'
  } else if (name == ONBOARDING_PROFESSIONAL_ROLES.OTHERS) {
    return 'Other'
  } else {
    return name
  }
}

onMounted(async () => {
  if (props.prefilledValues?.occupation) {
    selectedOption.value = props.prefilledValues?.occupation
  }
})
</script>

<template>
  <div class="flex max-h-full min-h-screen w-full flex-1 flex-col">
    <div class="flex max-h-full min-h-screen w-full flex-1 p-1 sm:p-0">
      <div class="m-5 hidden w-full rounded-3xl bg-[#EBEFF6] md:hidden lg:block lg:min-w-[45%]">
        <OnboardingProfessionalDetailsBanner />
      </div>

      <div
        class="flex h-screen w-full flex-col justify-between gap-4 rounded-md pb-6 pt-5 lg:min-w-[50%] lg:gap-2"
      >
        <div class="h-[85%] p-5">
          <!-- form -->
          <form @submit.prevent="onSubmit" class="flex h-full w-full flex-col justify-between">
            <div>
              <h1
                class="mb-5 mt-2 text-sm font-medium leading-[21px] sm:mb-10 lg:text-2xl lg:font-semibold lg:leading-9"
              >
                What describes you the best?
              </h1>
              <div class="flex w-full flex-row flex-wrap justify-between gap-4">
                <template v-for="({ value }, index) in professionalRoles" :key="index">
                  <div class="flex w-full items-center lg:w-[48%]">
                    <div
                      class="flex w-full cursor-pointer items-center rounded-md border border-slate-300 bg-slate-100 py-4 pl-4 shadow-lg"
                      @mouseover="addBoxShadow"
                      @mouseleave="removeBoxShadow"
                    >
                      <input
                        type="radio"
                        :id="value.toLowerCase()"
                        name="user-type"
                        :value="value"
                        v-model="selectedOption"
                        class="hidden"
                        @change="handleChange"
                      />
                      <label
                        :for="value.toLowerCase()"
                        class="flex cursor-pointer items-center gap-2"
                      >
                        <div
                          class="relative flex h-6 w-6 items-center justify-center rounded-full border border-new-secondary-900"
                        >
                          <div
                            v-if="selectedOption === value"
                            class="flex h-4 w-4 items-center justify-center rounded-full bg-new-secondary-900"
                          ></div>
                        </div>
                        <div class="text-base font-medium leading-normal">
                          {{ showProfessionalNames(value) }}
                        </div>
                      </label>
                    </div>
                  </div>
                </template>
                <p :class="['p-xsmall ', 'error', , { hidden: !selectedOptionError }]">
                  {{ selectedOptionError }}
                </p>

                <BaseTextbox
                  v-if="showOthersTextBox"
                  class="col-span-1 w-full"
                  :placeholder="descriptionInput.placeholder"
                  v-model="description"
                  :error="descriptionError"
                  :is-small-label="true"
                  :is-bold-label="true"
                  :rows="2"
                  :isLightMode="true"
                  :isLightGray="false"
                />
                <p :class="['p-xsmall ', 'error', , { hidden: !showOthersTextBoxError }]">
                  {{ showOthersTextBoxError }}
                </p>
              </div>
            </div>
            <p
              :class="[
                'mt-10',
                'h-full',
                'p-normal',
                'text-sky-950',
                'text-center',
                { hidden: !isLoading }
              ]"
            >
              Updating your details. Please wait...
            </p>
            <p
              :class="[
                'mt-10',
                'h-full',
                'p-normal ',
                'text-center',
                'error',
                ,
                { hidden: !httpError }
              ]"
            >
              {{ httpError }}
            </p>
            <div class="mt-6 flex w-full justify-start">
              <div class="flex w-full flex-col-reverse lg:w-fit xl:flex-row xl:gap-5">
                <Button
                  @click="handleSkip"
                  variant="link"
                  class="min-w-fit self-start px-0 xl:self-center"
                >
                  Prefer not to say
                </Button>
                <div class="mb-6 flex gap-2.5 xl:mb-0">
                  <Button
                    @click="handlePrevious"
                    color="secondary"
                    variant="outline"
                    size="medium"
                    class="h-[48px] sm:w-[171px]"
                  >
                    Previous
                  </Button>

                  <Button
                    type="submit"
                    :disabled="!canSubmit"
                    color="secondary"
                    variant="filled"
                    size="medium"
                    class="h-[48px] sm:w-[171px]"
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
